import { createApiClient } from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import type { LeaderboardEntry } from '@ergshoot/shared';
import { useCallback, useMemo, useRef } from 'react';

interface Score {
  id: string;
  workout_id: string;
  workout_name: string;
  value: number;
  division: string;
  score_display: string;
  scoring_type: string;
  notes: string | null;
  is_verified: boolean;
  created_at: string;
}

interface ScoreSubmitResult {
  id: string;
  workout_id: string;
  value: number;
  division: string;
  score_display: string;
  notes: string | null;
  position: number;
  total_entries: number;
}

export function useScoresApi() {
  const { getToken } = useAuth();

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const scoresApi = useMemo(() => {
    const client = createApiClient(stableGetToken);

    return {
      getScores: (params?: { workout_id?: string; limit?: number; offset?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.workout_id) queryParams.set('workout_id', params.workout_id);
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        if (params?.offset) queryParams.set('offset', params.offset.toString());
        const query = queryParams.toString();
        return client.get<{
          scores: Score[];
          pagination: { has_more: boolean; limit: number; offset: number };
        }>(`/scores${query ? `?${query}` : ''}`);
      },

      getScore: (id: string) => client.get<Score>(`/scores/${id}`),

      submitScore: (
        data: {
          workout_id: string;
          value: number | string;
          division?: string;
          notes?: string;
        },
        idempotencyKey?: string
      ) =>
        client.post<ScoreSubmitResult>('/scores', data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      updateScore: (
        id: string,
        data: {
          value?: number | string;
          division?: string;
          notes?: string;
        },
        idempotencyKey?: string
      ) =>
        client.put<{
          id: string;
          value: number;
          division: string;
          score_display: string;
          notes: string | null;
          updated_at: string;
        }>(`/scores/${id}`, data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      getLeaderboard: (
        workoutId: string,
        params?: {
          division?: string;
          affiliate_id?: string;
          limit?: number;
        }
      ) => {
        const queryParams = new URLSearchParams();
        if (params?.division) queryParams.set('division', params.division);
        if (params?.affiliate_id) queryParams.set('affiliate_id', params.affiliate_id);
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        const query = queryParams.toString();
        return client.get<{
          workout: {
            id: string;
            name: string;
            scoring_type: string;
            scoring_direction: string;
          };
          leaderboard: LeaderboardEntry[];
          total_entries: number;
          member_position: number | null;
        }>(`/scores/leaderboard/${workoutId}${query ? `?${query}` : ''}`);
      },
    };
  }, [stableGetToken]);

  return scoresApi;
}
